/**
 * A library document's history: what it was before each save, edit, file
 * replacement or restore, what changed since, and (for editors) a Restore
 * that keeps the current state as one more version first.
 */
import { Download, History, RotateCcw } from "lucide-react";
import { useEffect, useState, type FC } from "react";
import { toast } from "sonner";

import { firmKnowledgeApi, type FirmScope } from "@/api/firm-knowledge";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { formatMdY } from "@/features/clientProfile/lib/format";

import { CATEGORY_LABEL } from "../model";
import type {
  FirmLibraryCategory,
  FirmLibraryEntry,
  FirmLibraryVersion
} from "../types";

type Props = {
  entry: FirmLibraryEntry;
  scope: FirmScope;
  canEdit: boolean;
  onRestored: (entry: FirmLibraryEntry) => void;
  onDownload: (blob: Blob, filename: string) => void;
};

type VersionedField = keyof FirmLibraryVersion["fields"];

const FIELD_LABEL: Record<VersionedField, string> = {
  name: "Name",
  description: "Description",
  guidance: "Guidance",
  draft_type: "Draft type",
  visa: "Visa",
  visa_subtype: "Visa subtype",
  tags: "Tags",
  category: "Kind"
};

const display = (field: VersionedField, value: unknown): string => {
  if (Array.isArray(value)) return value.length ? value.join(", ") : "none";
  if (value === null || value === undefined || value === "") return "empty";
  if (field === "category")
    return CATEGORY_LABEL[value as FirmLibraryCategory] ?? String(value);
  return String(value);
};

const same = (a: unknown, b: unknown): boolean =>
  JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The fields that differ between the version and the entry as it is now. */
export const changedFields = (
  version: FirmLibraryVersion,
  entry: FirmLibraryEntry
): VersionedField[] =>
  (Object.keys(FIELD_LABEL) as VersionedField[]).filter(
    (field) =>
      field in version.fields && !same(version.fields[field], entry[field])
  );

const CLAMP = 140;
const clamp = (text: string) =>
  text.length > CLAMP ? `${text.slice(0, CLAMP).trimEnd()}…` : text;

export const EntryHistory: FC<Props> = ({
  entry,
  scope,
  canEdit,
  onRestored,
  onDownload
}) => {
  const [versions, setVersions] = useState<FirmLibraryVersion[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState<number | null>(null);
  const [confirming, setConfirming] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    firmKnowledgeApi
      .listEntryVersions(entry.id, scope)
      .then((rows) => {
        if (cancelled) return;
        setVersions(rows);
        setError(null);
      })
      .catch((e: unknown) => {
        if (!cancelled)
          setError(
            e instanceof Error ? e.message : "Could not load the history."
          );
      });
    return () => {
      cancelled = true;
    };
  }, [entry.id, entry.updated_at, scope]);

  const restore = async (version: FirmLibraryVersion) => {
    setWorking(version.version);
    try {
      const updated = await firmKnowledgeApi.restoreEntryVersion(
        entry.id,
        version.version,
        scope
      );
      onRestored(updated);
      setConfirming(null);
      toast.success(
        version.same_file_as_now
          ? "Restored the earlier details."
          : "Restored the earlier version; the preview is being rebuilt."
      );
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not restore.");
    } finally {
      setWorking(null);
    }
  };

  const download = async (version: FirmLibraryVersion) => {
    setWorking(version.version);
    try {
      const blob = await firmKnowledgeApi.getEntryVersionFile(
        entry.id,
        version.version,
        scope
      );
      onDownload(blob, version.original_filename ?? `${entry.name}`);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not download.");
    } finally {
      setWorking(null);
    }
  };

  if (error) return <p className="text-sm text-danger">{error}</p>;
  if (versions === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-fg-3">
        <Spinner className="size-3" /> Loading history…
      </div>
    );
  }
  if (versions.length === 0) {
    return (
      <p className="text-sm text-fg-3" data-testid="entry-history-empty">
        No earlier versions yet. Every save, edit or replaced file is kept here
        so it can be brought back.
      </p>
    );
  }

  return (
    <ol className="space-y-1.5" data-testid="entry-history">
      {versions.map((version) => {
        const changed = changedFields(version, entry);
        const fileChanged = version.has_file && !version.same_file_as_now;
        const busy = working === version.version;
        return (
          <li
            key={version.version}
            className="space-y-1.5 rounded-md border px-3 py-2"
            data-testid={`entry-version-${version.version}`}
          >
            <div className="flex items-start gap-2">
              <History className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
              <div className="min-w-0 flex-1">
                <p className="text-sm">
                  Before:{" "}
                  <span className="font-medium">{version.change_label}</span>
                </p>
                <p className="text-sm text-fg-3">
                  {formatMdY(version.created_at)}
                  {version.created_by_name
                    ? ` · ${version.created_by_name}`
                    : ""}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {fileChanged ? (
                  <Button
                    size="icon-sm"
                    variant="ghost"
                    className="size-7"
                    aria-label={`Download version ${version.version}`}
                    disabled={busy}
                    onClick={() => void download(version)}
                  >
                    <Download className="size-3.5" />
                  </Button>
                ) : null}
                {canEdit && (changed.length || fileChanged) ? (
                  <Button
                    size="sm"
                    variant="secondary"
                    className="h-7"
                    disabled={working !== null}
                    onClick={() =>
                      confirming === version.version
                        ? void restore(version)
                        : setConfirming(version.version)
                    }
                    data-testid={`restore-version-${version.version}`}
                  >
                    {busy ? (
                      <Spinner className="size-3" />
                    ) : (
                      <RotateCcw className="size-3.5" />
                    )}
                    {confirming === version.version
                      ? "Restore now?"
                      : "Restore"}
                  </Button>
                ) : null}
              </div>
            </div>
            {changed.length || fileChanged ? (
              <ul className="space-y-0.5 pl-5 text-sm text-fg-3">
                {fileChanged ? (
                  <li>
                    File: {version.original_filename ?? "an earlier file"}
                  </li>
                ) : null}
                {changed.map((field) => (
                  <li key={field} className="break-words">
                    {FIELD_LABEL[field]} was “
                    {clamp(display(field, version.fields[field]))}”
                  </li>
                ))}
              </ul>
            ) : (
              <p className="pl-5 text-sm text-fg-3">Same as now.</p>
            )}
          </li>
        );
      })}
    </ol>
  );
};
